/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated, type TranslationKey } from "@i18n";
import type { ComponentProps } from "react";
import type { ProjectData } from "@Reducers/breadcrumbsdata";
import { toObjectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import { useState, useEffect, useMemo } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { getProjectsStatusesData, saveProjectData } from "@Features/issues/state/bcfData.actions";
import { AddUsersToProject } from "@Features/projects";
import { DEFAULT_PROJECT_ROLES, getDefaultProjectRoles } from "@Helpers/roles";
import { showToastNotification } from "@Reducers/toastSlice";
import "./projectCreation.css";
import ProjectDetailsTemplate from "./ProjectDetails";
import TeamDetailsTemplate from "./TeamsDetails";
import Accordion from "@CubeComponents/html/accordion/materialUiAccordian";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router";
import ImageEditor from "@Components/ImageEditor";
import EditLocationModal from "./EditLocationModal";
import noLocation from "@Assets/images/noLocation.svg";
import { getCustomFields } from "@Services/utils/helpers";
import { useFormValidator } from "@Features/forms";
import { getImageUrl } from "@Utils";
import { Box } from "@mui/material";
import Text from "@CubeComponents/html/Text/Text";
import { RootState } from "@Reducers/index";

/** One step of the create-project accordion. */
type AccordionStep = { name: string; header: string; expanded: boolean };

const PROJECT_ROLE_SECTIONS = DEFAULT_PROJECT_ROLES.map((role) => ({
  roleName: role.frontendName,
  roleId: String(role.id),
})) satisfies readonly { roleName: TranslationKey; roleId: string }[];

/** One user picked for a project role, as the add-users modal hands them back. */
type SelectedTeamUser = { userId: string; fullName?: string; profilePic?: string };

interface CreateProjectProps {
  /** Closes the modal; `ProjectsList` re-fetches on it. */
  dialogClose?: () => void;
  /** Read only for the success-toast wording; no caller currently sets it. */
  editMode?: boolean;
}

function CreateProject(props: CreateProjectProps) {
  const { t } = useTranslation();
  const layout = useSelector((state: RootState) => state.layout);
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const [pickedUserIds, setPickedUserIds] = useState<ObjectIdString[]>([]);
  const defaultRoleOptions = useMemo(
    () => getDefaultProjectRoles(t).map(({ id, frontendName }) => ({ id: String(id), frontendName })),
    [t],
  );
  const excludedUsers = useMemo<ObjectIdString[]>(() => {
    const currentUserId = toObjectIdString(userDetails._id);
    return currentUserId ? [currentUserId, ...pickedUserIds] : pickedUserIds;
  }, [userDetails._id, pickedUserIds]);
  const formValidator = useFormValidator({});
  const initialAccordionData = [
    {
      name: "projectDetails",
      header: "Project Details",
      expanded: false,
    },
    {
      name: "imageAndLocation",
      expanded: false,
      header: "Image and Location",
    },
    {
      name: "teams",
      expanded: false,
      header: "Add Team",
    },
    {
      name: "allSet",
      expanded: false,
      header: "All Set!",
    },
  ];

  const [projectDetailsVisible, setProjectDetailsVisible] = useState(false);
  const [addUsersModalVisible, setAddUsersModalVisible] = useState(false);
  /**
   * The staged project plus the `formTemplate` answers this page folds into `customFields` before
   * save. `isDataFetched` is `ProjectData`'s only required member and nothing has been fetched yet,
   * so it starts `false`; everything else is filled in by the accordion steps below.
   */
  const [toSaveProjectData, setToSaveProjectData] = useState<
    ProjectData & {
      formTemplate?: Parameters<typeof getCustomFields>[0];
      customFields?: ReturnType<typeof getCustomFields>;
    } & ComponentProps<typeof EditLocationModal>["locationData"]
  >({ isDataFetched: false });
  const [disableCreateButton, setDisableCreateButton] = useState(true);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const [accordionData, setAccordionData] = useState<AccordionStep[]>(
    initialAccordionData.map((data: AccordionStep, index: number) => {
      if (index == 0) {
        data.expanded = true;
      }
      return data;
    }),
  );

  /** Role id → the users assigned to it. */
  const [selectedAddUserDetails, setSelectedAddUserDetails] = useState<Record<string, SelectedTeamUser[]>>({});
  const navigate = useNavigate();

  useEffect(() => {
    setAccordionData(
      initialAccordionData.map((data: AccordionStep, index: number) => {
        data.expanded = index == currentStage;
        return data;
      }),
    );
  }, [currentStage]);

  useEffect(() => {
    let enable = !!toSaveProjectData.projectId?.trim() && !!toSaveProjectData.description?.trim();
    setDisableCreateButton(!enable);
  }, [toSaveProjectData]);

  const dispatch = useDispatch();

  useEffect(() => {
    setProjectDetailsVisible(!projectDetailsVisible);
    dispatch(getProjectsStatusesData());
  }, []);

  const handleShowLocationModal = () => {
    setShowLocationModal(!showLocationModal);
  };

  const handleCreateProject = async () => {
    let selectedusersData: { userId: string; roleId: string }[] = [];
    for (const roleId of Object.keys(selectedAddUserDetails)) {
      const selectedRoleUserList = selectedAddUserDetails[roleId]!.map((data) => ({
        userId: data.userId,
        roleId,
      }));
      selectedusersData = [...selectedusersData, ...selectedRoleUserList];
    }

    const saveProjectData = {
      ...toSaveProjectData,
      selectedAddUserDetails: selectedusersData,
    };

    const res = await addProjectData(saveProjectData);
    if (res) {
      navigate("/projects");
    }
  };

  /**
   * `data` is mutated in place: the form-template answers are folded into `customFields` and the
   * template dropped before the request, neither of which `SaveProjectsRequest` declares.
   */
  const addProjectData = async (
    data: ProjectData & {
      formTemplate?: Parameters<typeof getCustomFields>[0];
      customFields?: ReturnType<typeof getCustomFields>;
    },
  ) => {
    if (data.formTemplate) {
      data.customFields = getCustomFields(data.formTemplate);
      delete data.formTemplate;
    }

    // The staged bag cannot be pared down to `SaveProjectsRequest` on this side: `projectDataItemSchema`
    // declares only 16 of the ~27 fields `saveProjectsData` persists (the whole location block,
    // `ministry`, `client`, …), and `EditLocationModal` writes `address` as a formatted string where
    // the request schema declares `string[]` (see the "frontend should send array" TODO in
    // project.mongo.service.ts). Both halves of the assertion are covered at runtime: the
    // `/saveProjectsData` route declares `schema: saveProjectsRequest`, whose Zod object drops every
    // undeclared key, and `projectId`/`description` — required there, optional on `ProjectData` — are
    // guaranteed non-empty by the Create button's `disableCreateButton` gate and, on the details
    // step, by `formValidator.validateAll()`.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- covered at runtime by the route's `schema: saveProjectsRequest` (Zod drops undeclared keys) and by the `disableCreateButton` gate on `projectId`/`description`.
    const response = await saveProjectData(data as typeof data & Parameters<typeof saveProjectData>[0]);
    if (response.status) {
      dispatch(
        showToastNotification({
          content: props.editMode
            ? t("Project {{name}} edited successfully", { name: data.projectId })
            : t("Project {{name}} created successfully", { name: data.projectId }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: response.error ? untranslated(response.error) : SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    return response.status;
  };

  const cancelProjectDetails = () => {
    navigate("/projects");
  };

  const handleNextStage = (e: React.MouseEvent<HTMLElement>, heading?: string) => {
    // Every button wired to this handler renders `data-index={index}`.
    const nextStage = Number(e.currentTarget.getAttribute("data-index")) + 1;
    if (heading == "projectDetails") {
      formValidator.validateAll() && setCurrentStage(nextStage);
    } else {
      setCurrentStage(nextStage);
    }
  };

  const openAddUsersModal = () => {
    setAddUsersModalVisible(!addUsersModalVisible);
  };

  const selectUser = (data: Record<string, SelectedTeamUser[]>) => {
    const userDetails = { ...selectedAddUserDetails };
    for (const id of Object.keys(data)) {
      if (userDetails[id]) {
        userDetails[id] = [...data[id]!, ...userDetails[id]!];
      } else {
        userDetails[id] = data[id]!;
      }
      const userIds: ObjectIdString[] = [];
      for (const user of data[id]!) {
        const pickedId = toObjectIdString(user.userId);
        if (!excludedUsers.includes(pickedId) && !userIds.includes(pickedId)) {
          userIds.push(pickedId);
        }
      }
      setPickedUserIds((prev) => [...prev, ...userIds]);
    }
    setSelectedAddUserDetails(userDetails);
  };

  const handleImageDelete = () => {
    setToSaveProjectData({
      ...toSaveProjectData,
      image: "",
      projectImageUrl: "",
    });
  };

  const handleImageUpdate = (data: string) => {
    setToSaveProjectData({
      ...toSaveProjectData,
      image: data,
      projectImageUrl: data,
    });
  };

  const handleLocationUpdate = (args: ComponentProps<typeof EditLocationModal>["locationData"]) => {
    setToSaveProjectData({
      ...toSaveProjectData,
      ...args,
    });
  };

  const accordionHeader = (header: string, index: number) => (
    <div className="flex gap-5 items-center">
      <span className="bg-button_light w-7 h-7 rounded-3xl flex items-center justify-center text-lg font-semibold">
        {index + 1}
      </span>
      <span className="text-lg font-semibold">{header}</span>
    </div>
  );

  const accordianContent = (heading: string, index: number) => {
    if (heading == "projectDetails") {
      return (
        <div className="ps-10 flex flex-col gap-14 pb-10">
          <ProjectDetailsTemplate
            formValidator={formValidator}
            setToSaveProjectData={setToSaveProjectData}
            toSaveProjectData={toSaveProjectData}
          />
          <div className="flex gap-10">
            <ButtonComponent
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              data-index={index}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                const res = formValidator.validateAll();
                if (res) {
                  handleNextStage(e, heading);
                }
              }}
              disabled={disableCreateButton}
              data-testid="saveAndNextBtnProjDetails"
            >
              <Text>{t("Save & Next")}</Text>
            </ButtonComponent>
            <ButtonComponent
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              onClick={cancelProjectDetails}
              data-testid="cancelBtnProjDetails"
            >
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </div>
        </div>
      );
    }
    if (heading == "imageAndLocation") {
      return (
        <div className="px-18 flex flex-col gap-14 pb-10">
          <div className="flex flex-row gap-4">
            <div className="">
              <div className="" data-testid="ProjectImage">
                <Text>{t("Project Image")}</Text>
              </div>
              <div
                className="w-74.96 p-6 rounded-lg"
                data-testid="projectImageWrapper"
                style={{
                  boxShadow: "0.25rem 0.25rem 0.9375rem 0 rgba(65,92,171,.15)",
                }}
              >
                <ImageEditor
                  isEditable
                  imageData={toSaveProjectData.image}
                  defaultImageData={getImageUrl() + (layout.PROJECT_LOGO ?? "")}
                  onSubmitFunction={handleImageUpdate}
                  onDelete={handleImageDelete}
                  heading={undefined}
                />
              </div>
            </div>
            <div className="w-full flex flex-col">
              <div>
                <Text data-testid="ProjectLocation">{t("Project Location")}</Text>
              </div>
              <div
                className="h-full p-6 rounded-lg flex justify-center"
                data-testid="ProjectLocationWrapper"
                style={{
                  boxShadow: "0.25rem 0.25rem 0.9375rem 0 rgba(65,92,171,.15)",
                }}
              >
                {!showLocationModal && !toSaveProjectData.coordinates && (
                  <div className="flex items-center justify-evenly">
                    <img src={noLocation} alt="NoImageToshow" className="h-[80%] w-[60%]" />
                    <Box
                      className="cursor-pointer font-bold text-[color:var(--gradient-color)]"
                      onClick={handleShowLocationModal}
                    >
                      <Text>{t("Select Location")}</Text>
                    </Box>
                  </div>
                )}

                {(showLocationModal || toSaveProjectData.coordinates) && (
                  <div className="w-full">
                    <EditLocationModal
                      locationData={toSaveProjectData}
                      onAddClick={handleLocationUpdate}
                      isEditable
                      onCancel={handleShowLocationModal}
                      openDirectModal
                    />
                    {toSaveProjectData.address && (
                      <div className="">
                        {/* `EditLocationModal`'s Save always rebuilds `address` as an array of lines,
                            but its map-pin path writes the single formatted string — hence the union. */}
                        {Array.isArray(toSaveProjectData.address)
                          ? toSaveProjectData.address.join(",")
                          : toSaveProjectData.address}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-10">
            <ButtonComponent
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              data-index={index}
              onClick={handleNextStage}
              data-testid="nextButtonForLocation"
            >
              <Text>{t("Next")}</Text>
            </ButtonComponent>
            <ButtonComponent
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              data-index={index}
              onClick={handleNextStage}
              data-testid="skipButtonForLocation"
            >
              <Text>{t("Skip")}</Text>
            </ButtonComponent>
          </div>
        </div>
      );
    }
    if (heading == "teams") {
      return (
        <TeamDetailsTemplate
          selectedAddUserDetails={selectedAddUserDetails}
          openAddUsersModal={openAddUsersModal}
          roles={PROJECT_ROLE_SECTIONS}
          index={index}
          handleNextStage={handleNextStage}
        />
      );
    }
    if (heading == "allSet") {
      return (
        <div className="mt-5 flex gap-4 ps-8 pb-2.5">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={handleCreateProject}
            disabled={disableCreateButton}
            data-testid="createAdvProjectBtn"
          >
            <Text>{t("Create Project")}</Text>
          </ButtonComponent>
          <ButtonComponent
            buttonType={BUTTON_TYPES.BORDER_BUTTON}
            onClick={cancelProjectDetails}
            data-testid="cancelBtnAdvproject"
          >
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      );
    }
  };

  const expandAccordianHandler = (clickedIndex: number) => {
    setAccordionData(
      accordionData.map((data: AccordionStep, index: number) => {
        if (index == clickedIndex) {
          data.expanded = !data.expanded;
        }
        return data;
      }),
    );
  };

  return (
    <>
      <div className="flex flex-col gap-12 pt-4 pe-24 mb-10 ps-18 overflow-auto w-full">
        <div className="headerTextWrapper">
          <Text className="text-4xl font-bold text-[#192048]" data-testid="advanceProjectText">
            {t("New Project")}
          </Text>
          <div className="flex justify-between text-sm text-[#192048]">
            <Text data-testid="advanceProjectDesc">
              {t(
                "Complete the below form to create a new project. Optionally, specify a location, project image and add the team who will work on this project.",
              )}
            </Text>
          </div>
        </div>
        <div className="fieldWrapper">
          <div className="flex flex-col h-full w-full p-100 gap-12">
            {accordionData.map(({ name, expanded, header }: AccordionStep, index: number) => (
              <Accordion
                key={header}
                expanded={expanded}
                sx={{ boxShadow: "0.25rem 0.25rem 0.9375rem 0px #415CAB26" }}
                onChange={() => expandAccordianHandler(index)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className="ps-8 pe-11 py-3">
                  {accordionHeader(header, index)}
                </AccordionSummary>
                <AccordionDetails>{accordianContent(name, index)}</AccordionDetails>
              </Accordion>
            ))}
          </div>
        </div>
      </div>
      {addUsersModalVisible && (
        <AddUsersToProject
          source={"portalSetting"}
          closeModal={openAddUsersModal}
          selectUser={selectUser}
          defaultRoleOptions={defaultRoleOptions}
          excludedUsers={excludedUsers}
          projectId={undefined}
          refreshAlreadyAddedUserData={undefined}
          customOnNext={undefined}
          groupId={undefined}
        />
      )}
    </>
  );
}

export default CreateProject;
